import { Localized } from "@fluent/react";
import CloseIcon from "@mui/icons-material/Close";
import { DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { CancelButton } from "../../../app/components/CancelButton";
import { SaveButton } from "../../../app/components/SaveButton";
import { useAppDispatch } from "../../../app/hooks";
import { activateSupplierCollaboration } from "../suppliersSlice";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { AppId } from "../../../app/appTypes";

interface ActivateCollaborationDialogProps {
    supplierId: AppId;
    onClose: () => void;
    onSuccess: () => void;
}

export const ActivateCollaborationDialog = ({ supplierId, onClose, onSuccess }: ActivateCollaborationDialogProps) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);

    const handleConfirm = useCallback(async () => {
        try {
            setLoading(true);
            const result = await dispatch(activateSupplierCollaboration(supplierId));
            if (activateSupplierCollaboration.fulfilled.match(result)) {
                dispatch(showSuccess("saved"));
                onSuccess();
            } else {
                dispatch(showError("error"));
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setLoading(false);
        }
    }, [supplierId, dispatch, onSuccess]);

    return (
        <>
            <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Localized id="supplier-confirm-activate">_Confirm reactivation</Localized>
                <IconButton
                    onClick={onClose}
                    disabled={loading}
                    size="small"
                    sx={{ position: "absolute", right: 12, top: 12 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 3, px: 3 }}>
                <Stack spacing={2}>
                    <Typography variant="body1">
                        <Localized id="supplier-confirm-activate-desc">
                            _Are you sure you want to reactivate this supplier?
                        </Localized>
                    </Typography>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <SaveButton 
                    loading={loading} 
                    onClick={handleConfirm}
                    label={<Localized id="supplier-activate">_Activate</Localized>}
                />
                <CancelButton loading={loading} onClick={onClose} />
            </DialogActions>
        </>
    );
};
